/**
 * Revision Selector for the Workflow tab builder header.
 *
 * Behaviour:
 * - Single revision → static: muted, no caret, not interactive. Stays rendered so
 *   the header doesn't reflow when a second revision later appears.
 * - Multiple revisions → interactive dropdown showing, newest-first:
 *   - Active revision: checked + ACTIVE badge + date range ("Current revision")
 *   - Past revisions: "Revision N" + date range + "promoted '…'" note when applicable
 *
 * Thin adapter over the shared {@link RevisionDropdown}: maps the workflow's
 * selectedRevisionId (null = active) onto the shared option model and back.
 */

import React from 'react'
import { HistoryOutlined } from '@ant-design/icons'
import type { RevisionMeta } from '../journey-notifications/revision-insights'
import { revisionDisplayLabel } from '../journey-notifications/revision-insights'
import { RevisionDropdown, RevisionDropdownOption } from '../journey-notifications/revision-dropdown'

export interface RevisionSelectorProps {
    /**
     * Ordered list of revision metadata (ascending ordinal, from buildRevisionMetaList).
     * Single-element list → static mode. Empty list → hidden.
     */
    revisions: RevisionMeta[]
    /** The id of the currently selected revision (null = active/current). */
    selectedRevisionId: number | null
    /** Called when the user selects a different revision. null = back to active. */
    onSelect: (revisionId: number | null) => void
    /** Optional: revision id → promoted variant label, for the "promoted '…'" note. */
    promotedVariantLabelByRevisionId?: Map<number, string>
}

/** Human-readable trigger label for the currently selected revision (shared helper). */
function currentRevisionLabel(revisions: RevisionMeta[], selectedRevisionId: number | null): string {
    if (revisions.length === 0) return 'Current'
    const meta =
        selectedRevisionId === null
            ? revisions.find((m) => m.isActive)
            : revisions.find((m) => m.id === selectedRevisionId)
    return meta ? revisionDisplayLabel(meta) : 'Current'
}

export const RevisionSelector: React.FC<RevisionSelectorProps> = ({
    revisions,
    selectedRevisionId,
    onSelect,
    promotedVariantLabelByRevisionId,
}) => {
    if (revisions.length === 0) return null

    const isMultiRevision = revisions.length > 1
    const activeRevision = revisions.find((m) => m.isActive)

    const triggerText = currentRevisionLabel(revisions, selectedRevisionId)
    const selectedKey =
        selectedRevisionId === null
            ? String(activeRevision?.id ?? revisions[revisions.length - 1].id)
            : String(selectedRevisionId)

    // Newest-first: active revision at top.
    const options: RevisionDropdownOption[] = [...revisions].reverse().map((meta) => {
        const promotedLabel = promotedVariantLabelByRevisionId?.get(meta.id)
        const promotedNote = promotedLabel ? `promoted '${promotedLabel}' · ` : ''
        return {
            key: String(meta.id),
            primary: revisionDisplayLabel(meta),
            sub: `${promotedNote}${meta.dateRange}`,
            showActiveBadge: meta.isActive,
        }
    })

    const handleSelect = (key: string) => {
        const meta = revisions.find((m) => String(m.id) === key)
        onSelect(meta?.isActive ? null : Number(key))
    }

    return (
        <RevisionDropdown
            data-testid="revision-selector"
            rootClassName={`revision-selector revision-selector--${isMultiRevision ? 'multi' : 'static'}`}
            ariaLabel="Revision history"
            icon={<HistoryOutlined style={{ fontSize: 13 }} />}
            prefix="Revision:"
            triggerText={triggerText}
            menuHeader="Revision history"
            options={options}
            selectedKey={selectedKey}
            onSelect={handleSelect}
            staticMode={!isMultiRevision}
            staticTooltip={
                !isMultiRevision
                    ? "This journey has a single revision. As the journey changes over time, past revisions will appear here to view."
                    : undefined
            }
        />
    )
}
